import { HttpClient, HttpHeaders, type HttpResponse } from '@angular/common/http';
import { type ElementRef, inject, Injectable } from '@angular/core';
import { FormBuilder, type FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, type UrlSegment } from '@angular/router';

import { Store } from '@ngrx/store';
import { catchError, combineLatest, iif, map, type Observable, of, switchMap, throwError } from 'rxjs';

import { CorporateUserRole, type Profile } from '@fcom/core-api/login';
import type { AppState } from '@fcom/core/interfaces';
import { loginToken, profile } from '@fcom/core/selectors';
import { ConfigService, MonitoringLogger } from '@fcom/core/services';
import { EMAIL_PATTERN, LocalDate, PHONE_PATTERN } from '@fcom/core/utils';
import { retryWithBackoff } from '@fcom/rx';
import { VatReceiptContactDetails, VatReceiptResult, VatReceiptSearch } from '@fcom/service-forms-lazy/interfaces';
import type { AttachmentResponse } from '@fcom/ui-components/components/forms/interfaces';
import { LanguageService } from '@fcom/ui-translate';
import { base64EncodeUnicode } from '@fcom/ui-components/utils/string.utils';

import type {
  Company,
  CompanyUpdate,
  ContactPersonUpdate,
  CorporateContactUs,
  CorporateSAF,
  CorporateSalesForceUser,
  CorporateUser,
  CorporateUserForm,
  Data,
  DataRequest,
  DataResponse,
  EnrollCorporate,
  SelectOption,
  TripsRequest,
  TripsResponse,
} from '../../interfaces';
import { fileToBase64 } from '../../utils/form-utils';
import { finnairPlusNumberValidator } from '../../utils/form-validators';
import { ScrollService } from '../scroll/scroll.service';

export const TERMS_AND_CONDITIONS_LINK = 'https://cdn.finnair.com/finnair-for-business-terms-and-conditions.pdf';

export const NAME_PATTERN = /^[a-z\-àáâäãåąčćęèéêëėįìíîïłńòóôöõøùúûüųūÿýżźñçšž∂ð ]+$/i;
export const ZIP_CODE_PATTERN = /^[a-zA-Z0-9 -]*$/;

@Injectable()
export class CorporateService {
  private store$ = inject<Store<AppState>>(Store);
  private configService = inject(ConfigService);
  private languageService = inject(LanguageService);
  private scrollService = inject(ScrollService);
  private http = inject(HttpClient);
  private monitoringLogger = inject(MonitoringLogger);
  private activatedRoute = inject(ActivatedRoute);
  private fb = inject(FormBuilder);

  apiUrl: string;

  constructor() {
    this.apiUrl = this.configService.cfg.corporateApiUrl;
  }

  sendContactCaseToSalesForce(payload: CorporateContactUs, userProfile: Profile): Observable<HttpResponse<Response>> {
    const url = userProfile?.corporate?.clientId
      ? `${this.apiUrl}/corporates/${userProfile.corporate.clientId}/contactUs`
      : `${this.apiUrl}/contactUs`;

    return this.getExtraHeaders(userProfile).pipe(
      switchMap((extraHeaders) => {
        return this.http
          .post<HttpResponse<Response>>(url, payload, this.getCorporateApiHeaders('application/json', extraHeaders))
          .pipe(
            catchError((err: unknown) => {
              this.monitoringLogger.warn('Failed to send contact case to corporate api', { error: err });
              return throwError(() => err);
            })
          );
      })
    );
  }

  postFile(file: File): Observable<AttachmentResponse> {
    return fileToBase64(file).pipe(
      switchMap((base64File) => {
        return this.http
          .post<AttachmentResponse>(
            `${this.apiUrl}/attachment`,
            {
              fileName: file.name,
              fileData: base64File,
            },
            this.getCorporateApiHeaders()
          )
          .pipe(
            catchError((err: unknown) => {
              this.monitoringLogger.warn('Failed to post file to corporate api', { error: err });
              return throwError(() => err);
            })
          );
      })
    );
  }

  enrollNewCorporate(payload: EnrollCorporate): Observable<HttpResponse<Response>> {
    return this.http
      .post<HttpResponse<Response>>(`${this.apiUrl}/corporates`, payload, this.getCorporateApiHeaders())
      .pipe(
        catchError((err: unknown) => {
          this.monitoringLogger.warn('Failed to post new corporate to corporate api', { error: err });
          return throwError(() => err);
        })
      );
  }

  getCompanyDetails(): Observable<Company> {
    return combineLatest([this.store$.pipe(profile()), this.store$.pipe(loginToken())]).pipe(
      switchMap(([userProfile, token]: [Profile, string]) => {
        return this.http.get<Company>(
          `${this.apiUrl}/corporates/${userProfile.corporate?.clientId}`,
          this.getCorporateApiHeaders('application/json', { oauth_token: token })
        );
      })
    );
  }

  hasActivatedSafForCompany(): Observable<boolean> {
    return combineLatest([this.store$.pipe(profile()), this.store$.pipe(loginToken())]).pipe(
      switchMap(([userProfile, token]: [Profile, string]) =>
        this.http.get<CorporateSAF>(
          `${this.apiUrl}/corporates/${userProfile.corporate?.clientId}/saf`,
          this.getCorporateApiHeaders('application/json', { oauth_token: token })
        )
      ),
      map((companyPermission) => companyPermission?.enabled ?? false)
    );
  }

  hasActivatedSafForUser(): Observable<boolean> {
    return combineLatest([this.store$.pipe(profile()), this.store$.pipe(loginToken())]).pipe(
      switchMap(([userProfile, token]: [Profile, string]) =>
        this.http.get<CorporateSAF>(
          `${this.apiUrl}/corporates/${userProfile.corporate?.clientId}/users/${userProfile.corporate.userId}/saf`,
          this.getCorporateApiHeaders('application/json', { oauth_token: token })
        )
      ),
      map((userPermission) => userPermission?.enabled ?? false)
    );
  }

  hasActivatedSaf(hasSafForCompany$: Observable<boolean>, hasSafForUser$: Observable<boolean>): Observable<boolean> {
    return combineLatest([hasSafForCompany$, hasSafForUser$]).pipe(map((activations) => activations.every(Boolean)));
  }

  toggleSafPermissions(
    enabled: boolean,
    hasActivatedForCompany$: Observable<boolean>,
    hasActivatedForUser$: Observable<boolean>
  ): Observable<string> {
    return combineLatest([
      hasActivatedForCompany$,
      hasActivatedForUser$,
      this.store$.pipe(profile()),
      this.store$.pipe(loginToken()),
    ]).pipe(
      switchMap(
        ([hasActivatedForCompany, hasActivatedForUser, userProfile, token]: [boolean, boolean, Profile, string]) =>
          this.activateSafForCompany(enabled, hasActivatedForCompany, userProfile.corporate.clientId, token).pipe(
            switchMap(() =>
              this.activateSafForUser(
                enabled,
                hasActivatedForUser,
                userProfile.corporate.clientId,
                userProfile.corporate.userId,
                token
              )
            )
          )
      )
    );
  }

  private activateSafForCompany(
    enabled: boolean,
    hasActivatedForCompany: boolean,
    clid: string,
    oauth_token: string
  ): Observable<string> {
    return iif(
      () => hasActivatedForCompany,
      of(null),
      this.http.post(
        `${this.apiUrl}/corporates/${clid}/saf`,
        { enabled },
        { ...this.getCorporateApiHeaders('application/json', { oauth_token }), responseType: 'text' }
      )
    );
  }

  private activateSafForUser(
    enabled: boolean,
    hasActivatedForUser: boolean,
    clid: string,
    userId: string,
    oauth_token: string
  ): Observable<string> {
    return iif(
      () => hasActivatedForUser,
      of(''),
      this.http.post(
        `${this.apiUrl}/corporates/${clid}/users/${userId}/saf`,
        { enabled },
        { ...this.getCorporateApiHeaders('application/json', { oauth_token }), responseType: 'text' }
      )
    );
  }

  updateCompanyDetails(payload: CompanyUpdate): Observable<HttpResponse<void>> {
    return combineLatest([this.store$.pipe(profile()), this.store$.pipe(loginToken())]).pipe(
      switchMap(([userProfile, token]: [Profile, string]) => {
        return this.http.patch<HttpResponse<void>>(
          `${this.apiUrl}/corporates/${userProfile.corporate?.clientId}`,
          payload,
          this.getCorporateApiHeaders('application/json', { oauth_token: token })
        );
      })
    );
  }

  updateContactPersons(payload: ContactPersonUpdate): Observable<HttpResponse<void>> {
    return combineLatest([this.store$.pipe(profile()), this.store$.pipe(loginToken())]).pipe(
      switchMap(([userProfile, oauth_token]: [Profile, string]) => {
        return this.http.post<HttpResponse<void>>(
          `${this.apiUrl}/corporates/${userProfile.corporate?.clientId}/contacts`,
          payload,
          this.getCorporateApiHeaders('application/json', { oauth_token })
        );
      })
    );
  }

  getUsers(): Observable<CorporateUser[]> {
    return combineLatest([this.store$.pipe(profile()), this.store$.pipe(loginToken())]).pipe(
      switchMap(([userProfile, token]: [Profile, string]) => {
        return this.http.get<CorporateUser[]>(
          `${this.apiUrl}/corporates/${userProfile.corporate?.clientId}/users`,
          this.getCorporateApiHeaders('application/json', { oauth_token: token })
        );
      })
    );
  }

  getUser(id: string): Observable<CorporateUser> {
    return this.store$.pipe(loginToken()).pipe(
      switchMap((token: string) => {
        return this.http.get<CorporateUser>(
          `${this.apiUrl}/users/${id}`,
          this.getCorporateApiHeaders('application/json', { oauth_token: token })
        );
      })
    );
  }

  addUser(newUser: CorporateSalesForceUser): Observable<void> {
    return combineLatest([this.store$.pipe(profile()), this.store$.pipe(loginToken())]).pipe(
      switchMap(([userProfile, token]: [Profile, string]) => {
        return this.http.post<void>(
          `${this.apiUrl}/corporates/${userProfile.corporate?.clientId}/users`,
          newUser,
          this.getCorporateApiHeaders('application/json', { oauth_token: token })
        );
      })
    );
  }

  updateUser(id: string, user: CorporateSalesForceUser): Observable<void> {
    return this.store$.pipe(loginToken()).pipe(
      switchMap((token: string) => {
        return this.http.patch<void>(
          `${this.apiUrl}/users/${id}`,
          user,
          this.getCorporateApiHeaders('application/json', { oauth_token: token })
        );
      })
    );
  }

  deactivateUser(userDetails: { id: string; lastname: string }): Observable<void> {
    return this.store$.pipe(loginToken()).pipe(
      switchMap((token: string) => {
        return this.http.delete<void>(
          `${this.apiUrl}/users/${userDetails.id}?lastName=${userDetails.lastname}`,
          this.getCorporateApiHeaders('application/json', { oauth_token: token })
        );
      })
    );
  }

  getTrips(params: TripsRequest): Observable<TripsResponse> {
    return combineLatest([this.store$.pipe(profile()), this.store$.pipe(loginToken())]).pipe(
      switchMap(([userProfile, oauthToken]: [Profile, string]) => {
        return this.http
          .post<TripsResponse>(
            `${this.apiUrl}/corporates/${userProfile.corporate.clientId}/trips`,
            params,
            this.getCorporateApiHeaders('application/json', { oauth_token: oauthToken })
          )
          .pipe(
            catchError((err: unknown) => {
              this.monitoringLogger.warn('Failed to get trips from corporate api', { error: err });
              return throwError(() => err);
            })
          );
      })
    );
  }

  getSpend(spendRequest: DataRequest): Observable<Data> {
    return combineLatest([this.store$.pipe(profile()), this.store$.pipe(loginToken())]).pipe(
      switchMap(([userProfile, oauthToken]: [Profile, string]) => {
        return this.http
          .post<DataResponse>(
            `${this.apiUrl}/corporates/${userProfile.corporate.clientId}/reports`,
            spendRequest,
            this.getCorporateApiHeaders('application/json', { oauth_token: oauthToken })
          )
          .pipe(
            map((response: DataResponse) => ({ clientId: userProfile.corporate.clientId, response })),
            catchError((err: unknown) => {
              this.monitoringLogger.warn('Failed to get spend from corporate api', { error: err });
              return of({ clientId: userProfile.corporate.clientId, error: err } as Data);
            })
          );
      })
    );
  }

  getVatReceipt(search: VatReceiptSearch): Observable<VatReceiptResult> {
    const params = this.getVatSearchParams(search);
    const url = `${this.apiUrl}/corporates/999999/receipts?${params}`;
    return this.http.get<VatReceiptResult>(url, this.getCorporateApiHeaders()).pipe(
      retryWithBackoff(2),
      catchError((err: unknown) => {
        this.monitoringLogger.warn('Failed to get VAT receipt', { error: err });
        return throwError(() => err);
      })
    );
  }

  getReceiptUrl(search: VatReceiptSearch, contactDetails: VatReceiptContactDetails): Observable<string> {
    const params = this.getVatSearchParams(search, 'PDF');

    if (this.configService.cfg.enableNewVatInvoice) {
      const token = base64EncodeUnicode(JSON.stringify({ ...search, ...contactDetails }));
      return of(`${this.apiUrl}/corporates/999999/receipts/?format=pdf&token=${token}&newInvoice=true`);
    }

    const url = `${this.apiUrl}/corporates/999999/receipts?${params}`;
    return this.http
      .post<{ receiptId?: string; base64?: string }>(url, contactDetails, this.getCorporateApiHeaders())
      .pipe(
        map((res) => {
          if (res.receiptId) {
            return `${this.apiUrl}/receipts/${res.receiptId}`;
          }
          throw new Error('No receiptId or base64 found in response');
        }),
        retryWithBackoff(2),
        catchError((err: unknown) => {
          this.monitoringLogger.warn('Failed to download the VAT receipt from corporate api', { error: err });
          return throwError(() => err);
        })
      );
  }

  getVatSearchParams(search: VatReceiptSearch, format = 'JSON'): URLSearchParams {
    const { lastName, pnr, ticketNumber } = search;
    const params = new URLSearchParams();
    params.append('format', format);
    params.append('lastName', lastName.toUpperCase());
    if (pnr) {
      params.append('pnr', pnr.toUpperCase());
    } else {
      params.append('ticketNumber', ticketNumber.replace(/\s/g, '').slice(-10));
    }
    if (search.newInvoice) {
      params.append('newInvoice', 'true');
    }
    return params;
  }

  /*
  This will return the parent page link, so last segment of current url will be removed.
  E.g. /fi-en/finnair-business-travel/join-corporate-form -> /fi-en/finnair-business-travel
   */
  getReturnLink(): Observable<string> {
    return this.activatedRoute.url.pipe(
      map((segments: UrlSegment[]) => {
        const paths = segments
          ?.slice(0, -1)
          .map((p) => p.path)
          .filter(Boolean)
          .join('/');
        return `${this.languageService.langValue}/${paths}`;
      })
    );
  }

  scrollToInvalidField(form: FormGroup | FormGroup[], elementRef: ElementRef): void {
    Array.isArray(form) ? form.forEach((f) => f.markAllAsTouched()) : form.markAllAsTouched();
    setTimeout(() => {
      this.scrollService.scrollToFirstInvalidInput(elementRef, 140, false, true);
    });
  }

  getCorporateUserForm(user?: CorporateUser, phoneNumber?: [string, string]): FormGroup<CorporateUserForm> {
    return this.fb.group({
      personalDetails: this.fb.group({
        salutation: [user?.title ?? '', Validators.required],
        firstName: [user?.firstname ?? '', [Validators.required, Validators.pattern(NAME_PATTERN)]],
        lastName: [user?.lastname ?? '', [Validators.required, Validators.pattern(NAME_PATTERN)]],
        email: [user?.email ?? '', [Validators.required, Validators.pattern(EMAIL_PATTERN)]],
        phonePrefix: [phoneNumber?.[0] ?? '', Validators.required],
        phoneNumber: [phoneNumber?.[1] ?? '', [Validators.required, Validators.pattern(PHONE_PATTERN)]],
        dateOfBirth: [user?.birthdate ? new LocalDate(user.birthdate) : undefined],
        workTitle: [user?.jobTitle ?? ''],
        fPlusNumber: [user?.finnairPlus ?? '', finnairPlusNumberValidator()],
      }),
      passportDetails: this.fb.group({
        passportNumber: [user?.passportNumber ?? ''],
        issueDate: [user?.issued ? new LocalDate(user.issued) : undefined],
        expirationDate: [user?.expires ? new LocalDate(user.expires) : undefined],
        nationality: [user?.nationality ?? ''],
      }),
      userDetails: this.fb.group({
        role: [user?.role ?? CorporateUserRole.TRAVELLER, Validators.required],
      }),
    });
  }

  getCountryInputOptions(): Observable<SelectOption[]> {
    return this.languageService.translate('countries').pipe(
      map((countries) =>
        Object.keys(countries)
          .map(
            (country) =>
              ({
                value: country.toUpperCase(),
                name: countries[country],
              }) as SelectOption
          )
          .sort((locationA, locationB) => {
            return locationA.name > locationB.name ? 1 : -1;
          })
      )
    );
  }

  getCountryListForTerms(): string[] {
    return [
      'AT',
      'BE',
      'CN',
      'CZ',
      'DK',
      'EE',
      'FI',
      'FR',
      'DE',
      'HK',
      'HU',
      'IN',
      'IE',
      'IT',
      'KR',
      'LV',
      'LT',
      'LU',
      'NL',
      'NO',
      'PL',
      'PT',
      'SG',
      'ES',
      'SE',
      'CH',
      'TH',
      'GB',
      'US',
    ];
  }

  private getExtraHeaders(userProfile: Profile | undefined): Observable<Record<string, string>> {
    if (userProfile) {
      return this.store$.pipe(
        loginToken(),
        map((token) => ({ oauth_token: token }))
      );
    }
    return of({});
  }

  private getCorporateApiHeaders(
    contentType = 'application/json',
    extraHeaders?: Record<string, string>
  ): { headers: HttpHeaders } {
    return {
      headers: new HttpHeaders({
        'X-Api-Key': this.configService.cfg.corporateApiKey,
        'Content-Type': contentType,
        ...(extraHeaders || {}),
      }),
    };
  }
}
